import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CountryDialPicker } from '@/shared/ui/components/country-dial-picker';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useAuthStore } from '@/auth/auth-store';
import {
  useCreateDoctor,
  useRolesByType,
  useSpecialityCodeList,
  buildCreateDoctorBody,
} from '@/settings/services';
import type { StaffSpeciality } from '@/types/settings-api';
import { cn } from '@/lib/utils';

/**
 * Add Practitioner sub-wizard (PR 4 — PROD-872, epic PROD-856).
 *
 * Flutter parity: `add_doctor_widget.dart` (the 2-step sub-wizard hosted
 * inside `add_doctors_widget.dart`). STEP 1 captures name + email + mobile
 * + role + specialities → fires POST /api/Doctor. STEP 2 in Flutter is a
 * working-hours profile editor; we render a confirmation pane and defer
 * the editor to Settings → Practitioners → Schedule. Same scope-trim
 * pattern as PR 2 (Practice working-hours deferral).
 *
 * Wire shape verified against working dev cURL 2026-05-21 — see
 * `use-create-doctor.ts` for the 14-field body invariants.
 *
 * Field order matches the Flutter widget (read top-to-bottom):
 *   1. PRACTITIONER DETAILS
 *      - First Name *      | Last Name *
 *      - Speciality *      | Role *
 *   2. ACCOUNT DETAILS
 *      - Email Address *   | Contact Number *
 */
export interface PractitionerAddSheetProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful create (Sheet closes itself). */
  onCreated?: () => void;
}

type Step = 1 | 2;

interface Draft {
  firstName: string;
  lastName: string;
  emailId: string;
  mobileNumber: string; // CountryDialPicker wire form: "+971-554654645"
  role: number | null;
  doctorSpecialities: StaffSpeciality[];
}

const EMPTY_DRAFT: Draft = {
  firstName: '',
  lastName: '',
  emailId: '',
  mobileNumber: '+971-',
  role: null,
  doctorSpecialities: [],
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_MIN_DIGITS = 7;

export function PractitionerAddSheet({
  open,
  onClose,
  onCreated,
}: PractitionerAddSheetProps) {
  const orgId = useAuthStore((s) => s.user?.organizationId);
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>(1);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [createdName, setCreatedName] = useState('');
  // Tracks whether a successful create happened — used to defer the list
  // invalidation to AFTER the Sheet unmounts (Samer review PR #323), so the
  // underlying list doesn't refetch while STEP 2 is still on screen.
  const [didCreate, setDidCreate] = useState(false);

  const createMutation = useCreateDoctor();
  const { data: roleRows = [] } = useRolesByType(3, orgId);

  // Active (non-archived) practitioner roles available for the dropdown.
  const roleOptions = useMemo(
    () =>
      roleRows
        .filter((r) => !r.isArchived)
        .map((r) => ({ value: r.roleId, label: r.roleName })),
    [roleRows],
  );

  // Reset on every open transition so Add → Add lands clean.
  useEffect(() => {
    if (open) {
      setStep(1);
      setDraft(EMPTY_DRAFT);
      setCreatedName('');
      setDidCreate(false);
    }
  }, [open]);

  // Deferred list invalidation — fires when the Sheet finishes closing AFTER
  // a successful create, NOT during the mutation's onSuccess. This prevents
  // the underlying list from re-rendering while STEP 2 is still visible
  // (Samer review PR #323). The user clicks Done → onOpenChange(false) →
  // didCreate is checked → invalidate fires → list refetches once Sheet is
  // unmounted.
  const handleSheetClose = () => {
    if (didCreate) {
      queryClient.invalidateQueries({ queryKey: ['settings', 'doctors-list'] });
    }
    onClose();
  };

  const phoneDigits = draft.mobileNumber.split('-')[1] ?? '';
  const isValid =
    draft.firstName.trim().length > 0 &&
    draft.lastName.trim().length > 0 &&
    EMAIL_REGEX.test(draft.emailId.trim()) &&
    phoneDigits.replace(/\D/g, '').length >= PHONE_MIN_DIGITS &&
    draft.role !== null &&
    draft.doctorSpecialities.length > 0;

  const isSaving = createMutation.isPending;

  const handleSave = () => {
    if (step === 2) {
      handleSheetClose();
      return;
    }
    if (!isValid || !orgId || draft.role === null) return;

    const body = buildCreateDoctorBody({
      organizationId: orgId,
      firstName: draft.firstName.trim(),
      lastName: draft.lastName.trim(),
      emailId: draft.emailId.trim(),
      mobileNumber: draft.mobileNumber,
      role: draft.role,
      doctorSpecialities: draft.doctorSpecialities,
    });

    createMutation.mutate(body, {
      onSuccess: () => {
        const fullName = `${draft.firstName.trim()} ${draft.lastName.trim()}`.trim();
        setCreatedName(fullName);
        setStep(2);
        setDidCreate(true);
        toast.success(`Invited ${fullName}`);
        onCreated?.();
      },
      onError: (err) =>
        toast.error(
          err instanceof Error ? err.message : 'Could not create practitioner.',
        ),
    });
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && handleSheetClose()}>
      <SheetContent
        side="right"
        className="w-full max-w-[720px] sm:max-w-[720px] overflow-y-auto scrollbar-hide p-0"
      >
        {/* Top bar with Cancel / Save — Flutter parity */}
        <SheetHeader className="flex flex-row items-center justify-between px-6 pt-6 pb-3 space-y-0">
          <button
            type="button"
            className="text-sm font-semibold text-[var(--accent-primary)] hover:underline disabled:opacity-50"
            onClick={handleSheetClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <SheetTitle className="[font:var(--type-card-title)] text-[var(--ink-body)]">
            Add Practitioner
          </SheetTitle>
          <button
            type="button"
            className="text-sm font-semibold text-[var(--accent-primary)] hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={handleSave}
            disabled={(step === 1 && (!isValid || isSaving)) || isSaving}
          >
            {step === 1 ? (isSaving ? 'Saving…' : 'Save') : 'Done'}
          </button>
        </SheetHeader>

        <StepIndicator step={step} />

        {step === 1 ? (
          <StepOnePane draft={draft} setDraft={setDraft} roleOptions={roleOptions} />
        ) : (
          <StepTwoPane createdName={createdName} />
        )}
      </SheetContent>
    </Sheet>
  );
}

function StepIndicator({ step }: { step: Step }) {
  return (
    <div className="flex gap-2 px-6 pb-4">
      <StepChip
        active={step === 1}
        past={step > 1}
        number="STEP 1"
        label="Add practitioner details"
      />
      <StepChip
        active={step === 2}
        past={false}
        number="STEP 2"
        label="Add working hours"
      />
    </div>
  );
}

function StepChip({
  active,
  past,
  number,
  label,
}: {
  active: boolean;
  past: boolean;
  number: string;
  label: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-1 items-center gap-2 rounded-[8px] border-2 px-3 py-2',
        active
          ? 'border-[var(--color-blue-500)]'
          : 'border-[var(--surface-line)]',
      )}
    >
      <div
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold',
          active
            ? 'bg-[var(--color-blue-500)] text-white'
            : past
              ? 'bg-[var(--color-status-success)] text-white'
              : 'bg-[var(--color-grey-100)] text-[var(--ink-meta)]',
        )}
      >
        {past ? <Check className="h-4 w-4" aria-hidden /> : number.slice(-1)}
      </div>
      <div className="flex flex-col leading-tight">
        <span
          className={cn(
            'text-xs font-medium',
            active
              ? 'text-[var(--accent-primary)]'
              : 'text-[var(--ink-meta)]',
          )}
        >
          {number}
        </span>
        <span
          className={cn(
            'text-sm font-semibold',
            active
              ? 'text-[var(--ink-body)]'
              : 'text-[var(--ink-meta)]',
          )}
        >
          {label}
        </span>
      </div>
    </div>
  );
}

function StepOnePane({
  draft,
  setDraft,
  roleOptions,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  roleOptions: Array<{ value: number; label: string }>;
}) {
  return (
    <div className="px-6 pb-10 space-y-6">
      <div className="rounded-card bg-[var(--color-surface-background)] p-4 text-center">
        <p className="[font:var(--type-body-strong)] text-[var(--accent-primary)]">
          You&rsquo;re Creating An Account For A Practitioner
        </p>
        <p className="mt-2 [font:var(--type-body)] text-[var(--ink-meta)]">
          After clicking &lsquo;Save&rsquo; Scrips will send an invite to this user
          so they can log into the practice and add their details
        </p>
      </div>

      <section>
        <h3 className="text-xs font-medium text-[var(--accent-primary)] mb-3">
          Practitioner details
        </h3>
        <div className="rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] p-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <FieldText
            label="First name"
            required
            value={draft.firstName}
            onChange={(v) => setDraft({ ...draft, firstName: v })}
          />
          <FieldText
            label="Last name"
            required
            value={draft.lastName}
            onChange={(v) => setDraft({ ...draft, lastName: v })}
          />
          <SpecialityField
            value={draft.doctorSpecialities}
            onChange={(next) => setDraft({ ...draft, doctorSpecialities: next })}
          />
          <RoleField
            options={roleOptions}
            value={draft.role}
            onChange={(v) => setDraft({ ...draft, role: v })}
          />
        </div>
      </section>

      <section>
        <h3 className="text-xs font-medium text-[var(--accent-primary)] mb-3">
          Account details
        </h3>
        <div className="rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] p-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <FieldText
            label="Email address"
            required
            type="email"
            value={draft.emailId}
            onChange={(v) => setDraft({ ...draft, emailId: v })}
          />
          <div className="flex flex-col gap-2">
            <Label text="Contact number" required />
            <CountryDialPicker
              value={draft.mobileNumber}
              onChange={(v) => setDraft({ ...draft, mobileNumber: v })}
              size="lg"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function StepTwoPane({ createdName }: { createdName: string }) {
  return (
    <div className="px-6 pb-10 pt-6 space-y-6">
      <div className="rounded-card border border-[var(--status-good)] bg-[var(--color-status-success-bg)] p-6">
        <div className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-full bg-[var(--color-status-success)] flex items-center justify-center flex-shrink-0">
            <Check className="h-5 w-5 text-white" aria-hidden />
          </div>
          <div>
            <p className="[font:var(--type-card-title)] text-[var(--status-good-text)]">
              {createdName || 'Practitioner'} added
            </p>
            <p className="mt-1 [font:var(--type-body)] text-[var(--status-good-text)]">
              An invite email has been sent. They&rsquo;ll appear in the
              practitioner list shortly.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-card border border-dashed border-[var(--surface-line)] bg-[var(--surface-read)] px-6 py-6 text-center">
        <p className="text-sm font-semibold text-[var(--ink-body)]">
          Configure working hours later
        </p>
        <p className="mt-2 [font:var(--type-body)] text-[var(--ink-meta)] max-w-md mx-auto">
          Working-hours profiles (Office / Virtual / Home schedules, exam
          rooms, holidays) can be configured from{' '}
          <span className="font-semibold text-[var(--ink-body)]">
            Settings → Practitioners → {createdName || 'this practitioner'} → Schedule
          </span>{' '}
          once the wizard finishes.
        </p>
      </div>
    </div>
  );
}

function FieldText({
  label,
  required,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'email';
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label text={label} required={required} />
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10"
      />
    </div>
  );
}

function Label({ text, required }: { text: string; required?: boolean }) {
  return (
    <label className="text-xs font-medium text-[var(--ink-meta)]">
      {text}
      {required && (
        <span className="ml-1 text-[var(--color-status-error)]">*</span>
      )}
    </label>
  );
}

function RoleField({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: number; label: string }>;
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <div className="flex flex-col gap-2">
      <Label text="Role" required />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <button
              type="button"
              className={cn(
                'h-10 flex items-center justify-between rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] px-3 text-sm',
                selected
                  ? 'text-[var(--ink-body)]'
                  : 'text-[var(--color-text-hint)]',
              )}
            />
          }
        >
          <span className="truncate">{selected?.label ?? 'Select role'}</span>
          {selected ? (
            <X
              className="h-4 w-4 text-[var(--ink-meta)] hover:text-[var(--ink-body)] cursor-pointer flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
              aria-label="Clear role"
            />
          ) : (
            <ChevronDown className="h-4 w-4 text-[var(--ink-meta)] flex-shrink-0" />
          )}
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-1"
        >
          {options.length === 0 ? (
            <p className="px-3 py-2 [font:var(--type-body)] text-[var(--ink-meta)]">
              No roles available
            </p>
          ) : (
            options.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center px-3 py-2 text-left text-sm rounded',
                  o.value === value
                    ? 'bg-[var(--accent-active-bg)] text-[var(--accent-active-text)]'
                    : 'hover:bg-[var(--color-surface-background)] text-[var(--ink-body)]',
                )}
              >
                {o.label}
              </button>
            ))
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

function SpecialityField({
  value,
  onChange,
}: {
  value: StaffSpeciality[];
  onChange: (next: StaffSpeciality[]) => void;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 300);
  const [open, setOpen] = useState(false);
  const { data: results = [], isLoading } = useSpecialityCodeList(debounced);

  const filtered = results.filter(
    (r) =>
      !value.some(
        (v) => v.specialitySkillId === r.specialitySkillId,
      ),
  );

  const handlePick = (r: StaffSpeciality) => {
    onChange([...value, r]);
    setQuery('');
  };

  const handleRemove = (skillId: string) => {
    onChange(value.filter((v) => v.specialitySkillId !== skillId));
  };

  return (
    <div className="flex flex-col gap-2 sm:col-span-2">
      <Label text="Speciality" required />
      <Popover open={open && debounced.length > 0} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <div
              className="flex flex-wrap items-center gap-2 rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] px-2 py-1.5 min-h-[40px] focus-within:ring-2 focus-within:ring-[var(--color-blue-500)]/30"
              onClick={() => setOpen(true)}
            />
          }
        >
          {value.map((s) => (
            <span
              key={s.specialitySkillId}
              className="inline-flex items-center gap-1 rounded-full bg-[var(--accent-active-bg)] px-3 py-0.5 text-sm text-[var(--accent-active-text)]"
            >
              {s.code.displayName ?? s.code.code}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(s.specialitySkillId);
                }}
                aria-label={`Remove ${s.code.displayName}`}
                className="hover:text-blue-900"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            placeholder={value.length === 0 ? 'Enter speciality' : ''}
            className="flex-1 min-w-[120px] border-0 bg-transparent text-sm outline-none focus:ring-0 placeholder:text-[var(--color-text-hint)]"
          />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-1 max-h-[280px] overflow-y-auto scrollbar-hide"
        >
          {isLoading ? (
            <p className="px-3 py-2 [font:var(--type-body)] text-[var(--ink-meta)]">
              Searching…
            </p>
          ) : filtered.length === 0 ? (
            <p className="px-3 py-2 [font:var(--type-body)] text-[var(--ink-meta)]">
              No matches
            </p>
          ) : (
            filtered.map((r) => (
              <button
                key={r.specialitySkillId}
                type="button"
                onClick={() => handlePick(r)}
                className="flex w-full items-center px-3 py-2 text-left text-sm rounded hover:bg-[var(--color-surface-background)] text-[var(--ink-body)]"
              >
                {r.code.displayName ?? r.code.code}
              </button>
            ))
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
